import React, { createContext, useContext, useCallback, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from './AuthContext';
import moduleCatalogueService from '../services/moduleCatalogueService';
import { CatalogueResponse, MyModules, ModuleDescriptor, NavSection, NavOverrides } from '../types/modules';

/**
 * Tenant module entitlements, app-wide.
 *
 * Server is the source of truth (`/api/v1/modules/mine`); this context is the
 * one place the sidebar, the <ModuleGuard> route wrapper and the marketplace
 * read "is module X enabled for this tenant". Mounted inside AuthProvider so it
 * fetches only once a user is signed in.
 *
 * `isModuleEnabled` is deliberately OPTIMISTIC while the entitlement query is
 * still loading — it returns true — so the sidebar doesn't flash every module
 * out and back in on first paint. Consumers that must not render early (the
 * route guard) read `isLoading` and show a spinner instead.
 */

interface ModulesContextType {
  catalogue: CatalogueResponse | null;
  modules: ModuleDescriptor[];
  sections: NavSection[];
  enabledModules: Set<string>;
  isModuleEnabled: (key: string) => boolean;
  navOverrides: NavOverrides;
  plan: MyModules['plan'] | null;
  billingCycle: MyModules['billingCycle'];
  isLoading: boolean;
  refresh: () => void;
}

const ModulesContext = createContext<ModulesContextType | undefined>(undefined);

export const ModulesProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const queryClient = useQueryClient();

  // The catalogue rarely changes within a session — cache it hard.
  const catalogueQuery = useQuery<CatalogueResponse>({
    queryKey: ['modules', 'catalogue'],
    queryFn: () => moduleCatalogueService.getCatalogue(),
    enabled: isAuthenticated,
    staleTime: Infinity,
  });

  const mineQuery = useQuery<MyModules>({
    queryKey: ['modules', 'mine'],
    queryFn: () => moduleCatalogueService.getMyModules(),
    enabled: isAuthenticated,
    staleTime: 60_000,
  });

  const enabledModules = useMemo(
    () => new Set<string>(mineQuery.data?.enabledModules ?? []),
    [mineQuery.data]
  );

  // Loading = the entitlement read hasn't resolved yet. Once it has, gating is
  // authoritative.
  const isLoading = isAuthenticated && mineQuery.isLoading;

  const isModuleEnabled = useCallback(
    (key: string) => (isLoading ? true : enabledModules.has(key)),
    [isLoading, enabledModules]
  );

  const refresh = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['modules', 'mine'] });
  }, [queryClient]);

  const value: ModulesContextType = useMemo(
    () => ({
      catalogue: catalogueQuery.data ?? null,
      modules: catalogueQuery.data?.modules ?? [],
      sections: catalogueQuery.data?.sections ?? [],
      enabledModules,
      isModuleEnabled,
      navOverrides: mineQuery.data?.navOverrides ?? {},
      plan: mineQuery.data?.plan ?? null,
      billingCycle: mineQuery.data?.billingCycle ?? 'MONTHLY',
      isLoading,
      refresh,
    }),
    [catalogueQuery.data, enabledModules, isModuleEnabled, mineQuery.data, isLoading, refresh]
  );

  return <ModulesContext.Provider value={value}>{children}</ModulesContext.Provider>;
};

export const useModules = (): ModulesContextType => {
  const ctx = useContext(ModulesContext);
  if (ctx === undefined) {
    throw new Error('useModules must be used within a ModulesProvider');
  }
  return ctx;
};
